/**
 * Client-side renderer for the KITE editor preview. Mirrors the
 * server-side renderer in `routes/public/kite-render.ts` closely enough
 * that the live preview looks like the published page. Doesn't apply
 * variable interpolation — the editor shows raw `{{var}}` tokens so the
 * author can see what placeholders they're inserting.
 */
import type {
  KiteFormField,
  KitePageBlock,
  KitePageDocument,
  KitePageTheme,
} from '@xsc/shared';

function blockStyle(block: KitePageBlock): React.CSSProperties {
  const s = block.style ?? {};
  return {
    background: s.background_color,
    color: s.text_color,
    fontSize: s.font_size_px ? `${s.font_size_px}px` : undefined,
    paddingTop: s.padding_y_px != null ? `${s.padding_y_px}px` : undefined,
    paddingBottom: s.padding_y_px != null ? `${s.padding_y_px}px` : undefined,
    paddingLeft: s.padding_x_px != null ? `${s.padding_x_px}px` : undefined,
    paddingRight: s.padding_x_px != null ? `${s.padding_x_px}px` : undefined,
    textAlign: s.align,
  };
}

function fontFamily(f: KitePageTheme['font_family']): string {
  switch (f) {
    case 'serif': return 'Georgia,Cambria,Times New Roman,serif';
    case 'mono': return 'ui-monospace,SFMono-Regular,Menlo,monospace';
    case 'inter': return 'Inter,system-ui,sans-serif';
    case 'roboto': return 'Roboto,system-ui,sans-serif';
    default: return 'system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif';
  }
}

function VideoEmbed({ url }: { url: string }) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
  if (yt && yt[1]) {
    return (
      <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
        <iframe
          src={`https://www.youtube.com/embed/${yt[1]}`}
          title="YouTube video"
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
          allowFullScreen
        />
      </div>
    );
  }
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo && vimeo[1]) {
    return (
      <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
        <iframe
          src={`https://player.vimeo.com/video/${vimeo[1]}`}
          title="Vimeo video"
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <video
      src={url}
      controls
      style={{ width: '100%', height: 'auto', borderRadius: 6 }}
    />
  );
}

function PreviewFormField({ f }: { f: KiteFormField }) {
  const common: React.CSSProperties = {
    padding: 10,
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: '1rem',
  };
  const label = (
    <span style={{ fontSize: '0.9rem', fontWeight: 500, marginBottom: 4 }}>
      {f.label}
      {f.required && ' *'}
    </span>
  );
  if (f.type === 'checkbox') {
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.95rem' }}>
        <input type="checkbox" disabled /> {f.label}{f.required && ' *'}
      </label>
    );
  }
  if (f.type === 'textarea') {
    return (
      <label style={{ display: 'flex', flexDirection: 'column' }}>
        {label}
        <textarea style={common} rows={4} placeholder={f.placeholder} disabled />
      </label>
    );
  }
  if (f.type === 'select') {
    return (
      <label style={{ display: 'flex', flexDirection: 'column' }}>
        {label}
        <select style={common} disabled>
          {(f.options ?? []).map((o) => <option key={o}>{o}</option>)}
        </select>
      </label>
    );
  }
  return (
    <label style={{ display: 'flex', flexDirection: 'column' }}>
      {label}
      <input type={f.type} style={common} placeholder={f.placeholder} disabled />
    </label>
  );
}

export function PreviewBlock({
  block,
  theme,
}: {
  block: KitePageBlock;
  theme: KitePageTheme;
}): JSX.Element {
  const wrap = (children: React.ReactNode) => (
    <section style={blockStyle(block)}>{children}</section>
  );
  switch (block.type) {
    case 'hero': {
      const heroStyle: React.CSSProperties = {
        padding: '64px 24px',
        textAlign: 'center',
        ...blockStyle(block),
        ...(block.props.background_image_url
          ? {
              backgroundImage: `url('${block.props.background_image_url}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              color: '#fff',
            }
          : {}),
      };
      return (
        <section style={heroStyle}>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 700, margin: '0 0 12px 0' }}>
            {block.props.headline}
          </h1>
          <p style={{ fontSize: '1.15rem', margin: 0, opacity: 0.9 }}>
            {block.props.subheadline}
          </p>
        </section>
      );
    }
    case 'text':
      return wrap(
        <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{block.props.body}</div>,
      );
    case 'image':
      if (!block.props.url) {
        return wrap(
          <div style={{
            border: '2px dashed #cbd5e1', padding: 32, textAlign: 'center',
            color: '#94a3b8', borderRadius: 6,
          }}>Image — add a URL in the inspector</div>,
        );
      }
      return wrap(
        <img
          src={block.props.url}
          alt={block.props.alt}
          style={{ display: 'block', margin: '0 auto', width: `${block.props.width_pct}%`, height: 'auto' }}
        />,
      );
    case 'button': {
      const isPrimary = block.props.variant === 'primary';
      return wrap(
        <div style={{ textAlign: 'center' }}>
          <a
            href={block.props.href}
            onClick={(e) => e.preventDefault()}
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              borderRadius: 6,
              background: isPrimary ? theme.accent_color : 'transparent',
              color: isPrimary ? '#fff' : theme.accent_color,
              border: `2px solid ${theme.accent_color}`,
              textDecoration: 'none',
              fontWeight: 600,
            }}
          >
            {block.props.label}
          </a>
        </div>,
      );
    }
    case 'form':
      return wrap(
        <form
          onSubmit={(e) => e.preventDefault()}
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          {block.props.fields.map((f) => <PreviewFormField key={f.name} f={f} />)}
          <button
            type="submit"
            style={{
              background: theme.accent_color,
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: 12,
              fontSize: '1rem',
            }}
          >
            {block.props.submit_label}
          </button>
        </form>,
      );
    case 'video':
      return wrap(
        <div>
          {block.props.url ? (
            <VideoEmbed url={block.props.url} />
          ) : (
            <div style={{
              border: '2px dashed #cbd5e1', padding: 48, textAlign: 'center',
              color: '#94a3b8', borderRadius: 6,
            }}>Video — paste a YouTube, Vimeo, or MP4 URL</div>
          )}
          {block.props.caption && (
            <p style={{ textAlign: 'center', fontSize: '0.9rem', marginTop: 8, opacity: 0.8 }}>
              {block.props.caption}
            </p>
          )}
        </div>,
      );
    case 'faq':
      return wrap(
        <div>
          {block.props.items.length === 0 && (
            <div style={{ color: '#94a3b8' }}>No FAQ items yet — add some in the inspector.</div>
          )}
          {block.props.items.map((q, i) => (
            <details key={i} style={{
              marginBottom: 8, border: '1px solid #e5e7eb', borderRadius: 6, padding: 12,
            }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{q.question}</summary>
              <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{q.answer}</div>
            </details>
          ))}
        </div>,
      );
    case 'spacer':
      return (
        <div style={{
          height: block.props.height_px,
          background: 'repeating-linear-gradient(45deg,#f1f5f9,#f1f5f9 6px,#fff 6px,#fff 12px)',
        }} />
      );
    case 'footer':
      return (
        <footer style={{
          marginTop: 48, padding: 24, textAlign: 'center', fontSize: '0.9rem', opacity: 0.7,
          ...blockStyle(block),
        }}>
          <div style={{ whiteSpace: 'pre-wrap' }}>{block.props.text}</div>
          {block.props.unsubscribe_url && (
            <p style={{ marginTop: 12, fontSize: '0.85rem' }}>
              <a href={block.props.unsubscribe_url} onClick={(e) => e.preventDefault()}
                 style={{ color: 'inherit', textDecoration: 'underline' }}>
                Unsubscribe
              </a>
            </p>
          )}
        </footer>
      );
  }
}

export function PreviewDocument({ doc }: { doc: KitePageDocument }): JSX.Element {
  return (
    <div style={{
      background: doc.theme.background_color,
      color: doc.theme.text_color,
      fontFamily: fontFamily(doc.theme.font_family),
      minHeight: '100%',
    }}>
      <div style={{ maxWidth: doc.theme.max_width_px, margin: '0 auto' }}>
        {doc.blocks.map((b) => <PreviewBlock key={b.id} block={b} theme={doc.theme} />)}
      </div>
    </div>
  );
}
